import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { GIButton, Modal } from 'app-components';

import { ModalContextType } from '../../../contexts/modalContext';
import { useStateCode } from '../../../hooks/useStateCode';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import Form from '../../../lib/wrappers/Form';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import Image from '../../../components/common/image/Image';
import CapitalizedButton from '../components/common/CapitalizedButton';

interface IModalProps extends ModalContextType {
    actualDatesEndOfCurrentMonth?: string;
    actualDatesEndOfNextMonth?: string;
    actualDatesEndOfMonthAfterNext?: string;
    disenrollGroup: {
        logoUrl: string;
        planName: string;
    }
    onSubmitDisEnrollment: Function
}

enum DISENROLLMENT_STEPS {
    CHOOSE_DATE = 'CHOOSE_DATE',
    CONFIRM_END_DATE = 'CONFIRM_END_DATE',
    CONFIRMATION = 'CONFIRMATION',
}

const StyledContainer = styled.div`
    border-left: 3px solid ${({ theme }) => theme.infoMain} !important;
    color : ${({ theme }) => theme.infoDarker} !important;
    background : ${({ theme }) => theme.infoLightest} !important;
`;

export default function disEnrollHistoryOptions(props: IModalProps) {
    const {
        actualDatesEndOfCurrentMonth,
        actualDatesEndOfNextMonth,
        actualDatesEndOfMonthAfterNext,
        disenrollGroup,
        onSubmitDisEnrollment,
        closeModal,
    } = props;

    const { isCancelDisenrollmentButtonVisible, isUnableToEnrollAgainWarningVisible } = useStateCode();
    const intl = useIntl();

    function onClose(e) {
        e?.preventDefault();
        closeModal('disEnrollHistoryOptions');
    }

    function onSubmit() {
        onSubmitDisEnrollment(disenrollmentData.disenrollOptionSelected);
        closeModal('disEnrollHistoryOptions');
    }

    const [currentStep, setCurrentStep] = useState(DISENROLLMENT_STEPS.CHOOSE_DATE);
    const [disenrollmentData, setDisenrollmentData] = useState({ disenrollDate: '', disenrollOptionSelected: '' });

    const disenrollDateConfig = {
        CURRENT_MONTH: actualDatesEndOfCurrentMonth,
        NEXT_MONTH: actualDatesEndOfNextMonth,
        MONTH_AFTER_NEXT_MONTH: actualDatesEndOfMonthAfterNext,
    };

    const headerConfig = {
        [DISENROLLMENT_STEPS.CHOOSE_DATE]: 'indportal.plansummary.selectTerminationDate',
        [DISENROLLMENT_STEPS.CONFIRM_END_DATE]: 'indportal.plansummary.confirmEndDate',
        [DISENROLLMENT_STEPS.CONFIRMATION]: 'indportal.plansummary.confirmation',
    };

    const footerConfig = {
        [DISENROLLMENT_STEPS.CHOOSE_DATE]: {
            buttonText: 'button.continue',
            onClick: () => setCurrentStep(DISENROLLMENT_STEPS.CONFIRM_END_DATE),
        },
        [DISENROLLMENT_STEPS.CONFIRM_END_DATE]: {
            buttonText: 'button.continue',
            onClick: () => setCurrentStep(DISENROLLMENT_STEPS.CONFIRMATION),
        },
        [DISENROLLMENT_STEPS.CONFIRMATION]: {
            buttonText: 'button.submit',
            onClick: onSubmit,
        },
    };

    const disenrollDateOptions = [
        {
            value: 'CURRENT_MONTH',
            condition: actualDatesEndOfCurrentMonth,
            text: intl.formatMessage(
                { id: 'indportal.plansummary.disenrollmentDates.lastDayCurrentMonth' },
                { actualDatesEndOfCurrentMonth },
            ),
        },
        {
            value: 'NEXT_MONTH',
            condition: actualDatesEndOfNextMonth,
            text: intl.formatMessage(
                { id: 'indportal.plansummary.disenrollmentDates.lastDayNextMonth' },
                { actualDatesEndOfNextMonth },
            ),
        },
        {
            value: 'MONTH_AFTER_NEXT_MONTH',
            condition: actualDatesEndOfMonthAfterNext,
            text: intl.formatMessage(
                { id: 'indportal.plansummary.disenrollmentDates.lastDayAfterMonth' },
                { actualDatesEndOfMonthAfterNext },
            ),
        },
    ];

    return (
        <Modal id="disenrollmentFlowModal">
            <Modal.Header id="disenrollmentFlowModalHeader" titleId={headerConfig[currentStep]} onHide={onClose} />
            <Modal.Body id="disenrollmentFlowModalBody">
                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CHOOSE_DATE}>
                    <Form>
                        <UniversalInput
                            label={{
                                isLabelVisible: false,
                            }}
                            inputData={{
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                liClass: ['text-left'],
                                currentData: disenrollmentData,
                                name: 'disenrollOptionSelected',
                                value: disenrollmentData.disenrollOptionSelected,
                                options: disenrollDateOptions.filter(o => !!o.condition),
                            }}
                            errorClasses={['margin-l-0']}
                            inputActions={{
                                onFieldChange: ({ disenrollOptionSelected }) =>
                                    setDisenrollmentData({
                                        disenrollOptionSelected,
                                        disenrollDate: disenrollDateConfig[disenrollOptionSelected],
                                    }),
                            }}
                        />
                    </Form>
                    <StyledContainer className="alert" dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'indportal.plansummary.needSpecificTermDate' }) }}>
                    </StyledContainer>
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CONFIRM_END_DATE}>
                    <p>
                        <FormattedMessage
                            id='indportal.plansummary.customGrouping.chosenEndDate.lastCoverageDate'
                            values={{
                                selectedCoverageDate: disenrollmentData.disenrollDate,
                                planName1: disenrollGroup.planName,
                                planName2: undefined
                            }}
                        />
                    </p>
                    <div className="margin20-t margin0-b disenroll-confirm-box">
                        <Image
                            source={disenrollGroup.logoUrl}
                            desc={disenrollGroup.planName}
                            name="chip"
                            classes={['pull-left']}
                        />
                    </div>
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CONFIRMATION}>
                    <StyledContainer className="alert">
                        <p dangerouslySetInnerHTML={{
                            __html: intl.formatMessage({ id: `indportal.plansummary.disenrollConfirmContent.coverageEndDate` },
                                {
                                    selectedCoverageDate: disenrollmentData.disenrollDate,
                                    planName1: disenrollGroup.planName,
                                    planName2: undefined
                                })
                        }}>
                        </p>
                    </StyledContainer>
                    <StyledContainer className="alert">
                        <FormattedMessage id="indportal.plansummary.note" />
                        <div className="margin-l-20">
                            <ul>
                                <li>
                                    <FormattedMessage id="indportal.plansummary.disenrollConfirmPt1" />
                                </li>
                                <ConditionalRendering isTrue={isUnableToEnrollAgainWarningVisible}>
                                    <li>
                                        <FormattedMessage id="indportal.plansummary.disenrollConfirmPt2" />
                                    </li>
                                </ConditionalRendering>
                            </ul>
                        </div>
                    </StyledContainer>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls>
                <ConditionalRendering
                    isTrue={isCancelDisenrollmentButtonVisible && currentStep === DISENROLLMENT_STEPS.CHOOSE_DATE}
                >
                    <CapitalizedButton 
                        id="disenrollmentFlowModalCancelButton" 
                        onClickHandler={onClose} 
                        variant="secondary" 
                        labelId="button.cancel" 
                    />
                </ConditionalRendering>

                <ConditionalRendering isTrue={currentStep === DISENROLLMENT_STEPS.CONFIRM_END_DATE}>
                    <CapitalizedButton
                        id="disenrollmentFlowModalUpdateButton"
                        onClickHandler={() => setCurrentStep(DISENROLLMENT_STEPS.CHOOSE_DATE)}
                        variant="secondary"
                        labelId="indportal.plansummary.updateTermDate"
                        leftMargin={true}
                    />
                </ConditionalRendering>

                <CapitalizedButton
                    id="disenrollmentFlowModalTextButton"
                    onClickHandler={footerConfig[currentStep].onClick}
                    variant="primary"
                    labelId={footerConfig[currentStep].buttonText}
                    isDisabled={!disenrollmentData.disenrollOptionSelected}
                    leftMargin={true}
                />
            </Modal.Controls>
        </Modal>
    );
}
